/**
 * EntrepriseLayout — wrapper des pages /entreprise/*
 *
 * V11 : badges sidebar sur Entretiens vidéo (invitations confirmées ou refusées
 * depuis < 7 jours) et Immersions (demandes acceptées par le candidat, charte en attente).
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { AuthLayoutShell } from './AuthLayoutShell';
import { SidebarLink } from '@/components/passage/SidebarLink';
import { useCompanyUnreadCount } from '@/hooks/useCompanyTracking';

function NewEventBadge({ count, label }: { count: number; label: string }) {
  if (count === 0) return null;
  return (
    <span
      aria-label={label}
      className="ml-auto inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-passage-outre px-1 text-[10px] font-bold text-white"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Badge entretiens : invitations auxquelles le candidat a répondu (accepté OU refusé)
 * au cours des 7 derniers jours.
 * Partage le cache ['entreprise', 'entretiens'] avec EntrepriseEntretiensPage via select.
 */
function EntrepriseInterviewBadge() {
  const { data = 0 } = useQuery({
    queryKey: ['entreprise', 'entretiens'],
    queryFn: () =>
      apiFetch<{ items: Array<{ status: string; updatedAt: string }> }>(
        '/entreprise/entretiens',
      ).catch(() => ({ items: [] as Array<{ status: string; updatedAt: string }> })),
    select: (r) => {
      const cutoff = Date.now() - SEVEN_DAYS_MS;
      return r.items.filter(
        (i) =>
          (i.status === 'accepted' || i.status === 'declined') &&
          new Date(i.updatedAt).getTime() > cutoff,
      ).length;
    },
    staleTime: 60_000,
  });
  return (
    <NewEventBadge
      count={data}
      label={`${data} entretien${data > 1 ? 's' : ''} avec réponse récente`}
    />
  );
}

/**
 * Badge immersions : demandes acceptées par le candidat (charte en attente de signature).
 * Partage le cache ['entreprise', 'immersions'] via select.
 */
function EntrepriseImmersionBadge() {
  const { data = 0 } = useQuery({
    queryKey: ['entreprise', 'immersions'],
    queryFn: () =>
      apiFetch<{ items: Array<{ status: string }> }>('/entreprise/immersions').catch(
        () => ({ items: [] as Array<{ status: string }> }),
      ),
    select: (r) => r.items.filter((i) => i.status === 'accepted').length,
    staleTime: 60_000,
  });
  return (
    <NewEventBadge
      count={data}
      label={`${data} immersion${data > 1 ? 's' : ''} acceptée${data > 1 ? 's' : ''}`}
    />
  );
}

/**
 * Badge candidatures : messages non-lus des candidats.
 * Endpoint dédié /entreprise/applications/unread-count — léger, sans pagination.
 */
function CompanyMessagesBadge() {
  const { data = 0 } = useCompanyUnreadCount();
  return (
    <NewEventBadge
      count={data}
      label={`${data} message${data > 1 ? 's' : ''} non lu${data > 1 ? 's' : ''} de candidats`}
    />
  );
}

export function EntrepriseLayout() {
  return (
    <AuthLayoutShell
      homePath="/entreprise"
      roleLabel="Entreprise"
      roleColor="outre"
      navAriaLabel="Navigation entreprise"
      sidebar={
        <>
          <SidebarLink to="/entreprise" end>Tableau de bord</SidebarLink>
          <SidebarLink to="/entreprise/recherche">Rechercher des candidats</SidebarLink>
          <SidebarLink to="/entreprise/besoins">Mes besoins</SidebarLink>
          <SidebarLink to="/entreprise/favoris">Favoris</SidebarLink>
          <SidebarLink to="/entreprise/recherches">Recherches sauvegardées</SidebarLink>
          <SidebarLink to="/entreprise/annonces">Mes annonces</SidebarLink>
          {/* V13 — CRM recrutement */}
          <SidebarLink to="/entreprise/candidatures">
            <span className="flex items-center justify-between gap-1">
              <span>Candidatures</span>
              <CompanyMessagesBadge />
            </span>
          </SidebarLink>
          <SidebarLink to="/entreprise/immersions">
            <span className="flex items-center justify-between gap-1">
              <span>Immersions</span>
              <EntrepriseImmersionBadge />
            </span>
          </SidebarLink>
          <SidebarLink to="/entreprise/entretiens">
            <span className="flex items-center justify-between gap-1">
              <span>Entretiens vidéo</span>
              <EntrepriseInterviewBadge />
            </span>
          </SidebarLink>
          <SidebarLink to="/entreprise/profil">Profil</SidebarLink>
          <SidebarLink to="/entreprise/aide">Aide</SidebarLink>
          <hr className="my-3 border-t-2 border-dashed border-passage-hairline" />
          <SidebarLink to="/compte/securite">Sécurité</SidebarLink>
          <SidebarLink to="/compte/donnees">Mes données</SidebarLink>
        </>
      }
    />
  );
}
